/**
 * Helpers to work with the agency hierarchy tree returned by the
 * `getAgencyTree` AWS endpoint (the same data the Hierarchy tab renders).
 * Tree nodes have the shape `{ AgencyID, AgencyName, Member_Agencies: [] }`.
 */

import { ONEILL_MAIN_AGENCY_ID } from 'constants/enum';

/**
 * Reduces a selection of agencies to the topmost ones per branch: an agency is
 * dropped when one of its ancestors in the tree is also selected, since the
 * ancestor's downline already covers it.
 *
 * Selected ids that are not found in the tree are kept, so the caller never
 * silently loses an agency.
 *
 * @param {object|object[]} roots - Tree root node(s), e.g. the `agenciesTree` redux value.
 * @param {Array<string|number>} selectedIds - Agency ids selected by the user.
 * @returns {string[]} The consolidated agency ids.
 */
export function consolidateAgenciesByHierarchy(roots, selectedIds) {
  const selected = new Set((selectedIds ?? []).map(String));
  const isOneillAgencySelected = selected.has(ONEILL_MAIN_AGENCY_ID);
  const filteredSelected = new Set(
    [...selected].filter(id => id !== ONEILL_MAIN_AGENCY_ID)
  );
  const kept = new Set();
  const seenInTree = new Set();

  const walk = (node, hasSelectedAncestor) => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach(child => walk(child, hasSelectedAncestor));
      return;
    }
    const nodeId = String(node.AgencyID ?? '');
    const isSelected = nodeId !== '' && filteredSelected.has(nodeId);
    if (nodeId !== '') seenInTree.add(nodeId);
    if (isSelected && !hasSelectedAncestor) kept.add(nodeId);
    walk(node.Member_Agencies, hasSelectedAncestor || isSelected);
  };

  walk(roots, false);

  for (const id of selected) {
    if (!seenInTree.has(id)) kept.add(id);
  }

  if (isOneillAgencySelected) kept.add(ONEILL_MAIN_AGENCY_ID);

  return [...kept];
}

/**
 * True when the tree is a single agency with no downline. Those users get
 * shortcut buttons instead of a collapsible tree, so there is nothing to
 * expand and nothing to find.
 *
 * @param {object[]} roots - Tree root node(s), e.g. the `agenciesTree` redux value.
 * @returns {boolean}
 */
export function isSingleAgencyWithoutDownline(roots) {
  if (!Array.isArray(roots) || roots.length !== 1) return false;
  return (roots[0]?.Member_Agencies?.length ?? 0) === 0;
}
